import React from 'react';
import './DocumentCard.css';

function DocumentCard({ document, onOpenDocument }) {
  // Extract all fields from the document
  const ucid = document.ucid || document.id || 'N/A';
  const title = document.ttl || document.title || 'Untitled Document';
  const type = document.type || 'Unknown';
  const similarityScore = document.similarity_score || 0;
  const assignee = document.normcorp || document.co || [];
  const inventor = document.inv || [];
  const meetingStartDate = document.dv_meeting_start_date || '';
  const meetingEndDate = document.dv_meeting_end_date || '';
  const similarity = document.technical_similarity || '';
  const difference = document.technical_difference || '';
  const summary = document.summary || '';
  const sourceUrl = document.source_url || '';
  const documentViewerUrl = document.document_viewer_url || sourceUrl;

  // Format arrays to strings
  const formatArray = (arr) => {
    if (!arr || arr.length === 0) return 'N/A';
    if (Array.isArray(arr)) {
      return arr.join(' | ');
    }
    return String(arr);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toISOString().split('T')[0];
    } catch {
      return dateStr;
    }
  };

  // Get similarity score color
  const getScoreColor = (score) => {
    if (score >= 80) return '#ffd700'; // Gold
    if (score >= 60) return '#ff8c00'; // Orange
    if (score >= 40) return '#1e90ff'; // Blue
    return '#808080'; // Gray
  };

  // Create ES direct link (assuming ES is at port 9200)
  const esDirectLink = `http://localhost:9200/your_index/_doc/${ucid}`;

  // Handle document link clicks
  const handleDocumentLinkClick = (event, url) => {
    event.preventDefault();
    if (onOpenDocument && url) {
      // Extract page number from URL hash
      const pageMatch = url.match(/#page=(\d+)/);
      const pageNumber = pageMatch ? parseInt(pageMatch[1], 10) : 1;
      onOpenDocument(url, pageNumber);
    }
  };

  // Process content to convert links to clean "Show" buttons
  const processContent = (content) => {
    if (!content) return '';

    // First, handle markdown-style links like [page 1](url) or [text](url)
    let processed = content.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (match, text, url) => {
        // Check if URL is complete or partial
        let fullUrl = url;
        if (!url.startsWith('http')) {
          // If URL is partial (like "show Jose/wg11/..."), fix it
          if (url.startsWith('show ')) {
            // Remove "show " prefix if present
            url = url.substring(5);
          }
          // Construct full URL based on common pattern
          fullUrl = `https://entsrch.dolcera.net/projects/0000000093/docs/${url}`;
        }

        // Extract page info from text (like "page 1", "page 4")
        const pageMatch = text.match(/page\s+(\d+)/i);
        const pageText = pageMatch ? `Page ${pageMatch[1]}` : 'Show';

        return `<a href="${fullUrl}" data-document-url="${fullUrl}" class="document-link">${pageText}</a>`;
      }
    );

    // Then handle bare URLs
    processed = processed.replace(
      /(^|[^"])(https?:\/\/[^\s,)]+(?:#page=\d+)?)/g,
      (match, prefix, url) => {
        // Extract page number from URL if present
        const pageMatch = url.match(/#page=(\d+)/);
        const linkText = pageMatch ? `Page ${pageMatch[1]}` : 'Show';
        return `${prefix}<a href="${url}" data-document-url="${url}" class="document-link">${linkText}</a>`;
      }
    );

    // Handle broken URLs that got split (like "show Jose/wg11/..." without proper link syntax)
    processed = processed.replace(
      /\b(show\s+)([^\s]+\.pdf(?:#page=\d+)?)/gi,
      (match, prefix, path) => {
        const fullUrl = `https://entsrch.dolcera.net/projects/0000000093/docs/${path}`;
        const pageMatch = path.match(/#page=(\d+)/);
        const linkText = pageMatch ? `Page ${pageMatch[1]}` : 'Show';
        return `<a href="${fullUrl}" data-document-url="${fullUrl}" class="document-link">${linkText}</a>`;
      }
    );

    return processed;
  };

  // Process all text fields
  const processedSummary = processContent(summary);
  const processedSimilarity = processContent(similarity);
  const processedDifference = processContent(difference);

  // Handle clicks on processed summary links
  const handleSummaryClick = (event) => {
    if (event.target.classList.contains('document-link')) {
      const documentUrl = event.target.getAttribute('data-document-url');
      if (documentUrl) {
        handleDocumentLinkClick(event, documentUrl);
      }
    }
  };

  return (
    <div className="patent-card">
      <div className="patent-header">
        <div className="patent-id-section">
          <a href={documentViewerUrl || esDirectLink} target="_blank" rel="noopener noreferrer" className="patent-id">
            {ucid}
          </a>
        </div>
        <div className="patent-badges">
          <span
            className="similarity-badge"
            style={{ backgroundColor: getScoreColor(similarityScore) }}
          >
            Similarity Score: {similarityScore}
          </span>
          <span className="type-badge">
            Type: {type.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="patent-title">
        {title}
      </div>

      <div className="patent-details">
        <div className="detail-row">
          <div className="detail-item">
            <strong>Assignee:</strong> {formatArray(assignee)}
          </div>
          <div className="detail-item">
            <strong>Inventor:</strong> {formatArray(inventor)}
          </div>
          <div className="detail-item">
            <strong>Meeting Date:</strong> {formatDate(meetingStartDate)}
            {meetingEndDate && meetingEndDate !== meetingStartDate && ` to ${formatDate(meetingEndDate)}`}
          </div>
        </div>
      </div>

      <div className="patent-analysis">
        <div className="analysis-section">
          <div className="similarity-section">
            <h4>Similarity</h4>
            {processedSimilarity ? (
              <div
                className="similarity-content"
                onClick={handleSummaryClick}
                dangerouslySetInnerHTML={{
                  __html: processedSimilarity
                }}
              />
            ) : (
              <p>No similarity analysis available.</p>
            )}
          </div>

          <div className="difference-section">
            <h4>Difference</h4>
            {processedDifference ? (
              <div
                className="difference-content"
                onClick={handleSummaryClick}
                dangerouslySetInnerHTML={{
                  __html: processedDifference
                }}
              />
            ) : (
              <p>No difference analysis available.</p>
            )}
          </div>

          {summary && (
            <div className="summary-section">
              <h4>Summary</h4>
              <div
                className="summary-content"
                onClick={handleSummaryClick}
                dangerouslySetInnerHTML={{
                  __html: processedSummary
                }}
              />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default DocumentCard;